import createDragDropHandler from 'common/drag-drop.util'
import { iconForMediaType } from 'common/media-icon'
import { labelForRole } from 'common/interval-roles'
import './scene-content.component.less'

export default function sceneContentComponent (
  $log, $q, $state, $stateParams, toaster, FileMediaService, ServiceAnnouncementsService,
  SocialMediaService, SceneCollectionService, SceneCollectionItemService, BulkApplyService, moment
) {
  'ngInject'
  return {
    restrict: 'E',
    scope: {},
    bindToController: {
      scene: '='
    },
    controller: () => { },
    controllerAs: 'ctrl',
    template: require('./scene-content.tpl.html').default,
    link: function ($scope, $element, attrs, ctrl) {
      ctrl.isCollapsed = false
      // The item list draws the icon of a media type, as Compose does.
      ctrl.typeIcon = iconForMediaType
      ctrl.playingSceneCollection = null
      ctrl.sceneCollections = []
      ctrl.isDeleting = false // Flag to track deletion in progress
      ctrl.showNewCollectionForm = false
      ctrl.newCollectionName = ''
      ctrl.editingCollectionId = null
      ctrl.editedCollectionName = ''
      ctrl.showCreateButton = true

      ctrl.getSceneCollections = () => {
        return SceneCollectionService
          .getSceneCollectionsForScene(ctrl.scene.id)
          .then(sceneCollectionsResponse => {
            const sceneCollectionsPromises = sceneCollectionsResponse.map(sceneCollection => {
              sceneCollection.intervals = sceneCollection.intervals.map(interval => {
                interval.readable_begin = moment(interval.start_datetime).format('DD/MM HH:mm')
                interval.readable_end = moment(interval.end_datetime).format('DD/MM HH:mm')
                return interval
              })

              return sceneCollection
            })

            return $q.all(sceneCollectionsPromises)
          })
          .then(sceneCollections => {
            ctrl.sceneCollections = sceneCollections
            return SceneCollectionService.getActiveSceneCollectionId(ctrl.scene.id)
          })
          .then(sceneCollectionId => {
            ctrl.playingSceneCollection = sceneCollectionId
          })
          .catch(err => {
            $log.error(`[SceneContent] getSceneCollections: ${err.message}`, err)
          })
      }

      const sceneCollectionUnsubscribe = SceneCollectionService.subscribe(() => {
        ctrl.getSceneCollections()
      })

      ctrl.activeSceneCollection = null
      ctrl.activeSceneCollectionItems = []
      ctrl.setActiveSceneCollection = function setActiveSceneCollection (sceneCollectionId) {
        SceneCollectionItemService
          .getSceneCollectionItemsForSceneCollection(sceneCollectionId)
          .then(response => {
            ctrl.activeSceneCollectionItems = response.items
            const totalDuration = response.totalDuration
            return SceneCollectionService.get(sceneCollectionId).then(sceneCollection => {
              ctrl.activeSceneCollection = sceneCollection
              ctrl.activeSceneCollection.totalDuration = totalDuration
              SceneCollectionService.setActiveSceneCollection(sceneCollectionId)
            })
          }).catch(err => {
            $log.error(`[SceneContent] setActiveSceneCollection: ${err.message}`, err)
          })
      }

      const sceneCollectionItemUnsubscribe = SceneCollectionItemService.subscribe(state => {
        if (ctrl.activeSceneCollection) {
          ctrl.setActiveSceneCollection(ctrl.activeSceneCollection.id)
        }
      })

      // The calendar's inspector links straight to a playlist's content. The
      // route param names which playlist to open on arrival.
      if ($stateParams.collection) {
        ctrl.setActiveSceneCollection(Number($stateParams.collection))

        // The param is an instruction, and it has been followed. Leaving it in
        // the URL made it stick: a transition inherits the current parameter
        // values, so every later move to a scene carried the param along and
        // opened a playlist the operator had not asked for. The param is
        // dynamic, so this clears the URL without entering the state again, and
        // it replaces the history entry so the back button does not restore it.
        $state.go('.', { collection: null }, { location: 'replace' })
      }

      ctrl.isIntervalActive = function (intervalEnd) {
        return new Date(intervalEnd) > new Date()
      }

      ctrl.getActiveIntervals = function (sceneCollection) {
        if (!sceneCollection.intervals || sceneCollection.intervals.length === 0) {
          return []
        }
        return sceneCollection.intervals.filter(interval => ctrl.isIntervalActive(interval.end_datetime))
      }

      // What the calendar's inspector calls the roles. The wire values never
      // reach the screen, and a value from before the roles has none to show.

      ctrl.roleLabel = function (interval) {
        return (interval && labelForRole(interval.role)) || null
      }

      ctrl.toggleIntervalTooltip = function (item) {
        // Close all other tooltips
        ctrl.sceneCollections.forEach(collection => {
          if (collection.id !== item.id) {
            collection.showIntervals = false
          }
        })
        // Toggle the clicked one
        item.showIntervals = !item.showIntervals
      }

      ctrl.removeActiveSceneCollection = function removeActiveSceneCollection () {
        ctrl.activeSceneCollection = null
        ctrl.activeSceneCollectionItems = []
        SceneCollectionService.removeActiveSceneCollection()
      }

      ctrl.deleteSceneCollection = function deleteSceneCollection (sceneCollectionId) {
        $log.info('🗑️ [SceneContent] User clicked delete button for SceneCollection (playlist) ID:', sceneCollectionId)

        if (ctrl.isDeleting) {
          return
        }

        if (confirm('Are you sure you want to delete the playlist?')) {
          ctrl.isDeleting = true
          SceneCollectionService
            .delete(sceneCollectionId)
            .catch(err => {
              $log.error(`❌ [SceneContent] deleteSceneCollection failed for ID ${sceneCollectionId}: ${err.message}`, err)
            })
            .finally(() => {
              ctrl.isDeleting = false
            })
        }
      }

      ctrl.editCollectionName = function editCollectionName (item) {
        ctrl.editingCollectionId = item.id
        ctrl.editedCollectionName = item.name
      }

      ctrl.saveCollectionName = function saveCollectionName (item) {
        if (!ctrl.editedCollectionName || !ctrl.editedCollectionName.trim()) {
          return
        }

        const name = ctrl.editedCollectionName.trim()
        SceneCollectionService.update({ id: item.id, name })
          .then(() => {
            ctrl.editingCollectionId = null
            ctrl.editedCollectionName = ''
          })
          .catch(err => {
            $log.error(`❌ [SceneContent] saveCollectionName failed: ${err.message}`, err)
          })
      }

      ctrl.cancelEditCollectionName = function cancelEditCollectionName () {
        ctrl.editingCollectionId = null
        ctrl.editedCollectionName = ''
      }

      ctrl.createNewSceneCollection = function createNewSceneCollection () {
        ctrl.showNewCollectionForm = true
        ctrl.showCreateButton = false
        ctrl.newCollectionName = ''
      }

      ctrl.saveNewSceneCollection = function saveNewSceneCollection () {
        if (ctrl.newCollectionName && ctrl.newCollectionName.trim()) {
          SceneCollectionService.create({
            name: ctrl.newCollectionName.trim()
          }).then(sceneCollection => {
            return SceneCollectionService.addSceneCollectionToScene(ctrl.scene.id, sceneCollection.id)
          }).then(() => {
            ctrl.showNewCollectionForm = false
            ctrl.newCollectionName = ''
            ctrl.showCreateButton = true
            // The subscription will update the list
          }).catch(err => {
            $log.error(`❌ [SceneContent] saveNewSceneCollection failed: ${err.message}`, err)
          })
        }
      }

      ctrl.cancelNewSceneCollection = function cancelNewSceneCollection () {
        ctrl.showNewCollectionForm = false
        ctrl.newCollectionName = ''
        ctrl.showCreateButton = true
      }

      ctrl.deleteSceneCollectionItem = function (id) {
        $log.info('🗑️ [SceneContent] User clicked delete button for SceneCollectionItem ID:', id)

        if (ctrl.isDeleting) {
          return Promise.resolve()
        }

        // Optimistic update - remove item immediately from UI
        ctrl.activeSceneCollectionItems = ctrl.activeSceneCollectionItems.filter(item => item.id !== id)

        ctrl.isDeleting = true
        return SceneCollectionItemService.delete(id)
          .catch(err => {
            $log.error(`❌ [SceneContent] Delete failed for ID ${id}, item will be restored via WebSocket: ${err.message}`, err)
            // Don't manually restore - let WebSocket subscription handle it
          })
          .finally(() => {
            ctrl.isDeleting = false
          })
      }

      // Add a property to store the copiedSceneCollectionId
      ctrl.copiedSceneCollectionId = SceneCollectionItemService.getCopiedSceneCollectionId()

      // Function to update copiedSceneCollectionId from the service
      ctrl.updateCopiedSceneCollectionId = function () {
        ctrl.copiedSceneCollectionId = SceneCollectionItemService.getCopiedSceneCollectionId()
      }

      // Watch for changes in the service and update the controller property
      $scope.$watch(function () {
        return SceneCollectionItemService.getCopiedSceneCollectionId()
      }, function (newVal, oldVal) {
        if (newVal !== oldVal) {
          ctrl.updateCopiedSceneCollectionId()
        }
      })

      // Function to store the ID of the SceneCollection to be copied
      ctrl.copySceneCollection = function (sceneCollectionId) {
        SceneCollectionItemService.setCopiedSceneCollectionId(sceneCollectionId)
        $log.info('Copied SceneCollection ID:', sceneCollectionId)
      }

      // Function to paste the copied SceneCollectionItems into another SceneCollection
      ctrl.pasteSceneCollectionItems = function (targetSceneCollectionId) {
        const sourceSceneCollectionId = SceneCollectionItemService.getCopiedSceneCollectionId()
        $log.info('Pasting SceneCollectionItems from:', sourceSceneCollectionId, 'to:', targetSceneCollectionId)
        if (!sourceSceneCollectionId || !targetSceneCollectionId) {
          $log.error('Both source and target SceneCollection IDs are required.')
          return
        }

        SceneCollectionItemService
          .copyItemsToSceneCollection(sourceSceneCollectionId, targetSceneCollectionId)
          .then(() => {
            $log.info('SceneCollectionItems pasted successfully.')
            // Clear copy buffer after successful paste
            SceneCollectionItemService.clearCopiedSceneCollectionId()
          })
          .catch(err => {
            $log.error(`[SceneContent] pasteSceneCollectionItems: ${err.message}`, err)
          })
      }

      // Function to delete all items in the active playlist
      ctrl.deleteAllSceneCollectionItems = function () {
        const itemCount = ctrl.activeSceneCollectionItems.length

        if (itemCount === 0) {
          $log.info('No items to delete in playlist')
          return
        }

        if (confirm(`Are you sure you want to delete all ${itemCount} items from this playlist? This cannot be undone.`)) {
          ctrl.isDeleting = true

          $log.info('🗑️ [SceneContent] User confirmed deletion of all items in playlist:', ctrl.activeSceneCollection.id)

          // No optimistic update - wait for WebSocket response
          SceneCollectionItemService
            .deleteAllItemsInPlaylist(ctrl.activeSceneCollection.id)
            .then(result => {
              $log.info(`✅ [SceneContent] Successfully deleted ${result.deletedCount} items from playlist`)
              // Clear copy buffer since copied items may have been deleted
              SceneCollectionItemService.clearCopiedSceneCollectionId()
              $log.info('Copy buffer cleared after delete all operation')
              // UI will update automatically via WebSocket subscription
            })
            .catch(err => {
              $log.error(`❌ [SceneContent] deleteAllSceneCollectionItems failed: ${err.message}`, err)
            })
            .finally(() => {
              ctrl.isDeleting = false
            })
        }
      }

      ctrl.durationIsEditable = function (sceneCollectionItem) {
        if (sceneCollectionItem.mediaType === 'file' && FileMediaService.getType(sceneCollectionItem.mediaId) === 'video') {
          // Disallow editing duration of FileMedia videos
          return false
        }

        return true
      }

      ctrl.validateAndSetDuration = function (id, duration) {
        let n = Math.floor(Number(duration))
        if (n > 0) {
          // Length is limited to 60 seconds (to ensure SM doesn't generate too long a queue in fillQueue)
          n = Math.min(n, 60)
          const newDuration = n * 1000
          return SceneCollectionItemService
            .update({
              id,
              duration: newDuration
            })
            .then(function () {
              return false
            })
            .catch(function (error) {
              $log.error(`Could not set duration for ${id}: `, error)
              return 'Error ' + error
            })
        }

        return 'Not a positive number.'
      }

      ctrl.playNow = function playNow (sceneCollectionId) {
        if (confirm('Are you sure you want to play this now?')) SceneCollectionService.playNow(sceneCollectionId)
      }

      // === Drag & Drop for the two lists ===
      // Where the dragged row would land. The template uses it to place the line.
      ctrl.collectionDrop = { index: null, from: null }
      ctrl.itemDrop = { index: null, from: null }

      /**
       * Send a reorder to the server.
       *
       * The handler names the two rows by the id in the DOM, so a value that is
       * not a number is a fault in this page and not a mistake of the operator.
       * The server reads the stored order itself, so the call needs no index.
       */
      function sendReorder (what, move, send) {
        const draggedId = Number(move.draggedId)
        const targetId = Number(move.targetId)
        // Number('') is zero, and no row of the database has that id.
        const isRowId = id => Number.isInteger(id) && id > 0

        if (!isRowId(draggedId) || !isRowId(targetId)) {
          $log.error(`[SceneContent] ${what}: a dragged row had no id`, move)
          toaster.error('The row could not be identified. Reload the page.', 'The move was not saved')
          return
        }

        // A refused move snaps the row back, and a row that snaps back without a
        // word looks like the page is broken.
        return send(draggedId, targetId, move.side).catch(err => {
          $log.error(`[SceneContent] ${what} failed: ${err.message}`, err)
          toaster.error(err.message, 'The move was not saved')
        })
      }

      // The two lists are on one element, so each one has its own row selector
      // and its own drag type. A playlist drag then never reorders an item. The
      // playlist list is short and scrolls with the page, so it asks for no
      // auto-scroll.
      const collectionDragDrop = createDragDropHandler({
        $scope,
        $element,
        $log,
        rowSelector: '[data-drag-collection]',
        dragType: 'text/collection-id',
        onReorder: move => sendReorder('reorderSceneCollections', move,
          (draggedId, targetId, side) => SceneCollectionService
            .reorderSceneCollections(ctrl.scene.id, draggedId, targetId, side)),
        setDropTarget: target => {
          ctrl.collectionDrop = target
        }
      })

      const dragDropHandler = createDragDropHandler({
        $scope,
        $element,
        $log,
        rowSelector: '[data-drag-item]',
        dragType: 'text/item-id',
        scrollContainerSelector: '.scene-content-scrollable',
        onReorder: move => sendReorder('reorderSceneCollectionItems', move,
          (draggedId, targetId, side) => SceneCollectionItemService
            .reorderSceneCollectionItems(ctrl.activeSceneCollection.id, draggedId, targetId, side)),
        setDropTarget: target => {
          ctrl.itemDrop = target
        }
      })

      collectionDragDrop.initialize()
      dragDropHandler.initialize()

      // Be sure to unsubscribe and destroy any selections that have been made
      $scope.$on('$destroy', () => {
        dragDropHandler.destroy()
        collectionDragDrop.destroy()

        sceneCollectionUnsubscribe()
        sceneCollectionItemUnsubscribe()
        SceneCollectionService.removeActiveSceneCollection()
      })
    }
  }
}
